/**
 * HeroTestimonialRotator — 4-second rotating quote strip.
 * Fetches live testimonials from /api/testimonials (managed via /admin/testimonials).
 * Falls back to PRICING_TESTIMONIALS if the endpoint returns <1 items.
 * Gracefully hides itself if there are literally zero quotes to show.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PRICING_TESTIMONIALS as FALLBACK } from "../data/pricing_testimonials";

const ROTATION_MS = 4000;
const AVATAR_COLORS = [
  "bg-purple-500/80 text-black",
  "bg-amber-400/80 text-black",
  "bg-emerald-500/80 text-black",
  "bg-rose-400/80 text-black",
  "bg-sky-400/80 text-black",
];

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/[\s&]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("");
}

export function HeroTestimonialRotator() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/testimonials");
        const live = Array.isArray(r?.data) ? r.data : r?.data?.testimonials || [];
        const clean = live.filter((t) => t.active !== false && t.quote);
        if (!cancelled && clean.length >= 1) {
          setItems(clean);
          return;
        }
        // Fallback to the vetted static list
        if (!cancelled) setItems(FALLBACK);
      } catch {
        if (!cancelled) setItems(FALLBACK);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATION_MS);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;

  const current = items[idx];
  const attrib = current.attribution || "Anonymous member";
  const tier = current.tier || "Member";
  const total = items.length;

  return (
    <div className="flex items-start gap-3" data-testid="hero-testimonial-rotator">
      {/* Avatar cluster (rotates lead avatar) */}
      <div className="flex -space-x-2 shrink-0" data-testid="hero-testimonial-avatars">
        {Array.from({ length: Math.min(3, total) }, (_, offset) => offset).map((offset) => {
          const item = items[(idx + offset) % total];
          const color = AVATAR_COLORS[(idx + offset) % AVATAR_COLORS.length];
          return (
            <div
              key={offset}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-black text-xs font-bold transition-[opacity,transform] duration-500 ${color} ${offset === 0 ? "z-10 scale-110" : "opacity-70"}`}
            >
              {initials(item.attribution)}
            </div>
          );
        })}
      </div>

      {/* Rotating quote */}
      <div className="min-h-[5rem] min-w-0 flex-1" data-testid="hero-testimonial-quote">
        <div key={current.id || idx} className="animate-in fade-in slide-in-from-bottom-1 duration-500">
          <p className="text-zinc-200 text-[15px] md:text-base leading-snug italic">
            &ldquo;{current.quote}&rdquo;
          </p>
          <p className="text-zinc-500 text-xs mt-1 tracking-wide">
            — <span className="text-purple-300">{attrib}</span>
            {current.location ? `, ${current.location}` : ""}
            <span className="text-zinc-600"> · {tier}</span>
            <span className="ml-2 text-zinc-700">
              {items.length > 1 ? `${idx + 1}/${items.length}` : "Verified member"}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default HeroTestimonialRotator;
